import { Box, Typography } from '@mui/material';
import Image from 'next/image';
import { env } from '@repo/env';
import { ElementViewed } from '@repo/segment/Analytics/ElementViewed';
import { FeatureViewed } from '@repo/segment/components/FeatureViewed';
import { BankingAssessmentBannerLink } from './BankingAssessmentBannerLink';
import {
  CALL_TO_ACTION_MESSAGE_MAP,
  HEADLINE_MESSAGE_PREFIX_MAP,
  HEADLINE_MESSAGE_PRODUCT_TYPE_MAP,
  SUBHEADLINE_MESSAGE_PRODUCT_TYPE_MAP,
} from './constant';
import { getImagesForBanner } from './getImagesForBanner';
import { getProductTypeFromSlug } from './getProductTypeFromSlug';

type BankingAssessmentBannerProps = {
  isTreatment1Enabled?: boolean;
  isTreatment2Enabled?: boolean;
  slug: string;
};

export function BankingAssessmentBanner({
  isTreatment1Enabled = false,
  isTreatment2Enabled = false,
  slug,
}: BankingAssessmentBannerProps) {
  if (!isTreatment1Enabled && !isTreatment2Enabled) {
    return null;
  }

  const productType = getProductTypeFromSlug(slug);
  if (!productType) {
    return null;
  }

  const variant = isTreatment1Enabled ? 'a' : 'b';
  const subheadlineMessage =
    SUBHEADLINE_MESSAGE_PRODUCT_TYPE_MAP[
      slug as keyof typeof SUBHEADLINE_MESSAGE_PRODUCT_TYPE_MAP
    ]?.[variant];
  const headlineMessageSuffix =
    HEADLINE_MESSAGE_PRODUCT_TYPE_MAP[slug as keyof typeof HEADLINE_MESSAGE_PRODUCT_TYPE_MAP]?.[
      variant
    ];
  if (!subheadlineMessage || !headlineMessageSuffix) {
    return null;
  }

  const callToActionMessage = isTreatment1Enabled
    ? CALL_TO_ACTION_MESSAGE_MAP.a
    : CALL_TO_ACTION_MESSAGE_MAP.b;

  const headlineMessagePrefix = isTreatment1Enabled
    ? HEADLINE_MESSAGE_PREFIX_MAP.a
    : HEADLINE_MESSAGE_PREFIX_MAP.b;

  const images = getImagesForBanner(slug);
  if (!images) {
    return null;
  }

  const image = isTreatment1Enabled ? images.a : images.b;
  const entityName = isTreatment1Enabled ? 'baf_falling_behind_banner' : 'baf_compare_banner';
  const actionName = isTreatment1Enabled
    ? 'baf_falling_behind_clicked'
    : 'baf_compare_banner_clicked';

  return (
    // TODO: Segment Standardization Cleanup
    <FeatureViewed entityName={entityName} entitySection="banking">
      <div>
        <ElementViewed entityName={entityName} sectionName="banking">
          <Box
            component="section"
            sx={{
              alignItems: 'center',
              background: 'linear-gradient(45deg, #013D2B 14.64%, #016B47 51.41%, #1E9E4C 85.36%)',
              borderRadius: '16px',
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              padding: {
                xs: '32px 24px 32px 24px',
                md: '32px 64px 32px 32px',
              },
              gap: '16px',
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  md: 'row',
                },
                gap: 3,
                width: {
                  xs: '100%',
                  md: 'auto',
                },
              }}
            >
              <Box
                sx={{
                  display: {
                    xs: 'flex',
                    md: 'none',
                  },
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Image
                  src={`${env.NEXT_PUBLIC_NW_URL}/cdn-cgi/image/format=auto,fit=scale-down,quality=85,sharpen=1/cdn/banking/banking-assessment-banner-images/white-complete-nerdwallet-logo.svg`}
                  alt="Nerdwallet and its logo"
                  height={21}
                  width={126}
                  unoptimized
                />
              </Box>
              <Box
                component="img"
                src={image.src}
                srcSet={image.srcSet}
                sizes={image.sizes}
                alt="Banner Image"
                sx={{
                  display: 'block',
                  height: {
                    xs: image.dimensions.mobile.height,
                    md: image.dimensions.desktop.height,
                  },
                  maxWidth: '100%',
                  objectFit: 'contain',
                  width: {
                    xs: image.dimensions.mobile.width,
                    md: image.dimensions.desktop.width,
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                textAlign: {
                  xs: 'center',
                  md: 'start',
                },
                flexGrow: {
                  md: 2,
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: 'white',
                  fontSize: '24px',
                  width: '100%',
                  letterSpacing: '-0.48px',
                  flexGrow: 1,
                  fontWeight: 500,
                  lineHeight: '104%',
                }}
              >
                {`${headlineMessagePrefix} `}
                <Typography
                  component="span"
                  sx={{ color: '#97CB65', fontSize: '24px', fontWeight: 500, lineHeight: '104%' }}
                >
                  {productType}
                </Typography>
                {` ${headlineMessageSuffix}`}
              </Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 350,
                  margin: 0,
                  flexGrow: 1,
                  lineHeight: '130%',
                }}
              >
                {subheadlineMessage}
              </Typography>
            </Box>
            <BankingAssessmentBannerLink
              actionName={actionName}
              callToActionMessage={callToActionMessage}
            />
          </Box>
        </ElementViewed>
      </div>
    </FeatureViewed>
  );
}
